import type { Survey } from 'posthog-js'

export function usePostHogSurvey(name: string) {
  const { $posthog } = useNuxtApp()
  const survey = useState<Survey | null | undefined>(
    `posthog-survey:${name}`,
  )
  const isResolving = useState(
    `posthog-survey:${name}:resolving`,
    () => false,
  )

  // Shared state, so the N answers rendering this survey resolve it once.
  //
  // `getSurveys`, not `getActiveMatchingSurveys`: the latter applies PostHog's
  // popover display policy (seen-once, wait periods, the internal targeting
  // flag), which permanently suppresses a `schedule: "once"` survey once the
  // user has been shown it. An `api` survey is rendered by the app, so the app
  // owns that decision — here we only need the survey to be running.
  onMounted(() => {
    if (survey.value !== undefined || isResolving.value)
      return

    isResolving.value = true

    function resolveFromSurveys(surveys: Survey[]) {
      const match = surveys.find(candidate => candidate.name === name)
      const startTime = match?.start_date ? Date.parse(match.start_date) : Number.NaN
      survey.value = match
        && Number.isFinite(startTime)
        && startTime <= Date.now()
        && !match.end_date
        ? match
        : null
      isResolving.value = false
    }

    $posthog.getSurveys((surveys, context) => {
      if (context?.isLoaded) {
        resolveFromSurveys(surveys)
        return
      }

      // Surveys not ready yet (or fetch failed). Wait for PostHog to finish
      // loading — `onSurveysLoaded` runs then, and immediately if already loaded.
      // No context means surveys are unavailable (e.g. disabled); give up.
      if (!context) {
        isResolving.value = false
        return
      }

      // `onSurveysLoaded` may run the callback synchronously. Defer the
      // unsubscribe handle so the callback never reads it before assignment.
      const subscription: { unsubscribe?: () => void } = {}
      let settled = false

      subscription.unsubscribe = $posthog.onSurveysLoaded((loadedSurveys, loadedContext) => {
        if (!loadedContext?.isLoaded) {
          isResolving.value = false
          return
        }

        resolveFromSurveys(loadedSurveys)
        settled = true
        subscription.unsubscribe?.()
      })

      if (settled)
        subscription.unsubscribe?.()
    })
  })

  return {
    survey: readonly(survey),
  }
}
